import { useEffect } from 'react'
import { consumePendingNavigation } from '@/shared/lib/pendingNavigation'

/**
 * Honours a destination the service worker parked when a push notification was
 * tapped (IN-12). Mounted once, inside AppLayout, so it only ever runs for a
 * signed-in member on an app route.
 *
 * Runs twice over, because the two shapes of the bug arrive differently:
 *   • on mount — the cold start, where the PWA launched (possibly ignoring the
 *     URL, as iOS does) and this is the first chance anyone has to read it;
 *   • on `visibilitychange` — the warm resume, where the app was already running
 *     and being focused is all that actually happened.
 *
 * Nothing happens when the app is already at the parked path: `openWindow` and
 * the worker's NAVIGATE message both land there on their own, and re-navigating
 * would push a pointless history entry.
 */
export function usePendingPushNavigation(navigate: (path: string) => void): void {
  useEffect(() => {
    let cancelled = false

    const consume = () => {
      // Captured BEFORE the async read, not after: on a cold start the app is
      // already at `/chat/12?msg=345` and ChatMessageList strips `?msg=` about
      // 150ms later, once it has done the jump. Comparing against the location
      // as it is when the cache resolves would see the stripped URL, decide it
      // had not arrived, and navigate a second time.
      const here = window.location.pathname + window.location.search + window.location.hash
      void consumePendingNavigation().then((path) => {
        if (cancelled || !path || path === here) return
        navigate(path)
      })
    }

    consume()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') consume()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [navigate])
}
